import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Truck, Pencil, X, Package } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { formatPrice } from '../lib/utils';
import { useShippingNatures } from '../hooks/useShippingNatures';

// 運費試算後台 v2（shipping-zones 2.0「定性配對」；Admin Form First）
// 三層：zones（目的地）→ rates（線種/價）→ natures（每區勾准許定性——白名單語義）
// ＋商品定性覆寫（空=繼承類目）。v1 的逐條品類 rules 已移除（規則行隨品類膨脹的根因）。
// 金額輸入以 HKD 顯示，送出前換算 cents（全站慣例：API 一律 cents）。

interface Rate {
  id: number; zoneId: number; label: string; baseCents: number;
  perKgCents: number | null; etaDays: string | null; sort: number;
}

interface Zone {
  id: number; name: string; code: string; enabled: boolean;
  sort: number; freeOverCents: number | null; rates: Rate[];
  allowedNatures: { id: number; code: string; nameZh: string }[] | null; // null＝准許全部
  foodAllowed: boolean | null;
}

interface ProductNatureRow {
  id: number; name: string; categorySlug: string | null; categoryName: string | null;
  isActive: boolean; overrideNatureId: number | null; effectiveNatureCode: string;
}

const emptyZoneForm = { name: '', code: '', sort: '0', freeOverHkd: '' };
const emptyRateForm = { label: '', baseHkd: '', perKgHkd: '', etaDays: '', sort: '0' };

export default function AdminShipping() {
  const { natures } = useShippingNatures();
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const [zoneForm, setZoneForm] = useState(emptyZoneForm);
  const [editingZoneId, setEditingZoneId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [rateForm, setRateForm] = useState(emptyRateForm);
  const [editingRateId, setEditingRateId] = useState<number | null>(null);

  // 定性勾選草稿（zoneId → Set<natureId>；null 表示未動）
  const [natureDraft, setNatureDraft] = useState<Map<number, Set<number>> | null>(null);
  const [productNatures, setProductNatures] = useState<ProductNatureRow[]>([]);
  const [natureOverrides, setNatureOverrides] = useState<Map<number, string>>(new Map()); // productId → 下拉值

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/shipping/zones');
      const json = await res.json();
      const list: Zone[] = Array.isArray(json) ? json : json?.data || [];
      setZones(list);
      setNatureDraft(null);
      if (selectedId && !list.some((z) => z.id === selectedId)) setSelectedId(null);
    } catch {
      setError('載入運費分區失敗');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProductNatures = useCallback(async () => {
    try {
      const res = await apiFetch('/api/public/admin/shipping/product-natures');
      const json = await res.json();
      const list: ProductNatureRow[] = Array.isArray(json) ? json : json?.data || [];
      setProductNatures(list);
      setNatureOverrides(new Map(list.map((p) => [p.id, p.overrideNatureId != null ? String(p.overrideNatureId) : ''])));
    } catch { /* 字典未就緒（migration 未跑）靜默 */ }
  }, []);

  useEffect(() => { loadProductNatures(); }, [loadProductNatures]);

  const flash = (ok: string) => { setMsg(ok); setError(''); setTimeout(() => setMsg(''), 2500); };
  const fail = async (res: Response) => {
    const j = await res.json().catch(() => ({ error: '操作失敗' }));
    setError(j.error || '操作失敗'); setTimeout(() => setError(''), 4000);
  };

  // ----- zones -----
  const submitZone = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = {
      name: zoneForm.name,
      code: zoneForm.code,
      sort: Number(zoneForm.sort || 0),
      freeOverCents: zoneForm.freeOverHkd ? Math.round(parseFloat(zoneForm.freeOverHkd) * 100) : null,
    };
    const res = editingZoneId
      ? await apiFetch(`/api/public/admin/shipping/zones/${editingZoneId}`, { method: 'PUT', body: JSON.stringify(body) })
      : await apiFetch('/api/public/admin/shipping/zones', { method: 'POST', body: JSON.stringify(body) });
    if (!res.ok) return fail(res);
    flash(editingZoneId ? '分區已更新' : '分區已建立');
    setZoneForm(emptyZoneForm); setEditingZoneId(null);
    load();
  };

  const toggleZone = async (z: Zone) => {
    const res = await apiFetch(`/api/public/admin/shipping/zones/${z.id}`, {
      method: 'PUT', body: JSON.stringify({ enabled: z.enabled ? 0 : 1 }),
    });
    if (!res.ok) return fail(res);
    flash(z.enabled ? `「${z.name}」已停用（前台不展示該區運費）` : `「${z.name}」已啟用`);
    load();
  };

  const deleteZone = async (z: Zone) => {
    if (!window.confirm(`刪除分區「${z.name}」？線種與准許定性會一併刪除，不可還原。`)) return;
    const res = await apiFetch(`/api/public/admin/shipping/zones/${z.id}`, { method: 'DELETE' });
    if (!res.ok) return fail(res);
    flash('分區已刪除');
    if (selectedId === z.id) setSelectedId(null);
    load();
  };

  const startEditZone = (z: Zone) => {
    setEditingZoneId(z.id);
    setZoneForm({
      name: z.name, code: z.code, sort: String(z.sort),
      freeOverHkd: z.freeOverCents != null ? (z.freeOverCents / 100).toString() : '',
    });
  };

  // ----- rates -----
  const submitRate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedId) return;
    const body = {
      label: rateForm.label,
      baseCents: Math.round(parseFloat(rateForm.baseHkd || '0') * 100),
      perKgCents: rateForm.perKgHkd ? Math.round(parseFloat(rateForm.perKgHkd) * 100) : null,
      etaDays: rateForm.etaDays,
      sort: Number(rateForm.sort || 0),
    };
    const res = editingRateId
      ? await apiFetch(`/api/public/admin/shipping/rates/${editingRateId}`, { method: 'PUT', body: JSON.stringify(body) })
      : await apiFetch(`/api/public/admin/shipping/zones/${selectedId}/rates`, { method: 'POST', body: JSON.stringify(body) });
    if (!res.ok) return fail(res);
    flash(editingRateId ? '線種已更新' : '線種已新增');
    setRateForm(emptyRateForm); setEditingRateId(null);
    load();
  };

  const deleteRate = async (r: Rate) => {
    if (!window.confirm(`刪除線種「${r.label}」？`)) return;
    const res = await apiFetch(`/api/public/admin/shipping/rates/${r.id}`, { method: 'DELETE' });
    if (!res.ok) return fail(res);
    flash('線種已刪除');
    load();
  };

  // ----- zone natures（勾選草稿＋保存） -----
  const selected = zones.find((z) => z.id === selectedId) || null;

  const draftFor = (z: Zone | null): Set<number> | null => {
    if (!z) return null;
    if (natureDraft?.has(z.id)) return natureDraft.get(z.id)!;
    return z.allowedNatures ? new Set(z.allowedNatures.map((n) => n.id)) : null; // null＝准許全部
  };

  const toggleNature = (z: Zone, natureId: number) => {
    const next = new Map(natureDraft || new Map());
    const current = next.get(z.id) ?? (z.allowedNatures ? new Set(z.allowedNatures.map((n) => n.id)) : new Set());
    if (current.has(natureId)) current.delete(natureId);
    else current.add(natureId);
    next.set(z.id, current);
    setNatureDraft(next);
  };

  const clearNatures = (z: Zone) => {
    const next = new Map(natureDraft || new Map());
    next.set(z.id, new Set());
    setNatureDraft(next);
  };

  const saveNatures = async (z: Zone) => {
    const draft = draftFor(z);
    const natureIds = draft ? Array.from(draft) : [];
    const res = await apiFetch(`/api/public/admin/shipping/zones/${z.id}/natures`, {
      method: 'PUT', body: JSON.stringify({ natureIds }),
    });
    if (!res.ok) return fail(res);
    flash(draft && draft.size ? `「${z.name}」准許定性已保存（白名單 ${draft.size} 項）` : `「${z.name}」已設為准許全部`);
    load();
  };

  // ----- 商品定性覆寫 -----
  const saveProductNature = async (productId: number) => {
    const value = natureOverrides.get(productId) ?? '';
    const body = { natureId: value ? Number(value) : null };
    const res = await apiFetch(`/api/public/admin/shipping/product-natures/${productId}`, {
      method: 'PUT', body: JSON.stringify(body),
    });
    if (!res.ok) return fail(res);
    flash('商品定性已更新');
    loadProductNatures();
  };

  const natureName = (code: string) => natures.find((n) => n.code === code)?.nameZh || code;

  const inputCls = 'border border-stone-300 rounded-xl px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-x1493-primary/30';
  const labelCls = 'text-xs font-bold text-stone-500 block mb-1';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Truck size={24} className="text-x1493-primary" />
          運費試算
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          跨境運費分區、線種與「定性配對」（貨品定性 × 地區准許清單）。分區停用＝前台不展示運費；定性配對即時生效。
        </p>
      </div>

      {msg && <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-2 text-sm">{msg}</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>}

      {/* 分區表單 */}
      <form onSubmit={submitZone} className="bg-white border rounded-2xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-bold">{editingZoneId ? '編輯分區' : '新增分區'}</h2>
          {editingZoneId && (
            <button type="button" onClick={() => { setEditingZoneId(null); setZoneForm(emptyZoneForm); }}
              className="text-sm text-stone-500 flex items-center gap-1 hover:text-stone-700">
              <X size={14} /> 取消
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className={labelCls}>名稱（例：英國）</label>
            <input className={inputCls} value={zoneForm.name} onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })} required />
          </div>
          <div>
            <label className={labelCls}>代碼（例：GB）</label>
            <input className={inputCls} value={zoneForm.code} onChange={(e) => setZoneForm({ ...zoneForm, code: e.target.value.toUpperCase() })} required maxLength={8} />
          </div>
          <div>
            <label className={labelCls}>排序</label>
            <input className={inputCls} type="number" value={zoneForm.sort} onChange={(e) => setZoneForm({ ...zoneForm, sort: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>滿 HK$ 免運（留空＝無）</label>
            <input className={inputCls} type="number" step="0.01" min="0" value={zoneForm.freeOverHkd} onChange={(e) => setZoneForm({ ...zoneForm, freeOverHkd: e.target.value })} />
          </div>
        </div>
        <button type="submit" className="btn-primary text-sm px-5 py-2 rounded-xl flex items-center gap-1">
          <Plus size={16} /> {editingZoneId ? '更新分區' : '新增分區（預設停用）'}
        </button>
      </form>

      {/* 分區列表 */}
      <div className="bg-white border rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-left">
            <tr>
              <th className="px-4 py-3">分區</th>
              <th className="px-4 py-3">代碼</th>
              <th className="px-4 py-3">准許定性</th>
              <th className="px-4 py-3">食品</th>
              <th className="px-4 py-3">線種</th>
              <th className="px-4 py-3">滿額免運</th>
              <th className="px-4 py-3">狀態</th>
              <th className="px-4 py-3 text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-stone-400">載入中...</td></tr>
            ) : zones.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-stone-400">尚未建立分區</td></tr>
            ) : zones.map((z) => {
              const draft = draftFor(z);
              return (
                <tr key={z.id} className={`border-t cursor-pointer ${selectedId === z.id ? 'bg-x1493-primary/5' : 'hover:bg-stone-50'}`}
                  onClick={() => setSelectedId(z.id === selectedId ? null : z.id)}>
                  <td className="px-4 py-3 font-bold">{z.name}</td>
                  <td className="px-4 py-3">{z.code}</td>
                  <td className="px-4 py-3 text-xs">
                    {draft === null
                      ? <span className="text-green-700 font-bold">全部</span>
                      : draft.size === 0
                        ? <span className="text-amber-600 font-bold">全部（未保存）</span>
                        : <span className="text-stone-600">{[...draft].map((id) => natures.find((n) => n.id === id)?.nameZh || id).join('、')}</span>}
                  </td>
                  <td className="px-4 py-3">
                    {z.foodAllowed === null ? <span className="text-stone-400 text-xs">—</span>
                      : z.foodAllowed ? <span className="text-green-600 font-bold">✅</span>
                      : <span className="text-amber-600 font-bold">⚠️</span>}
                  </td>
                  <td className="px-4 py-3">{z.rates.length} 線</td>
                  <td className="px-4 py-3">{z.freeOverCents != null ? formatPrice(z.freeOverCents) : '—'}</td>
                  <td className="px-4 py-3">
                    <button onClick={(e) => { e.stopPropagation(); toggleZone(z); }}
                      className={`px-2.5 py-1 rounded-full text-xs font-bold ${z.enabled ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                      {z.enabled ? '已啟用' : '停用'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={(e) => { e.stopPropagation(); startEditZone(z); }} className="p-1.5 text-stone-400 hover:text-x1493-primary" title="編輯"><Pencil size={15} /></button>
                    <button onClick={(e) => { e.stopPropagation(); deleteZone(z); }} className="p-1.5 text-stone-400 hover:text-red-500" title="刪除"><Trash2 size={15} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 選中分區：線種＋准許定性 */}
      {selected && (
        <div className="space-y-4">
          {/* 准許定性 */}
          <div className="bg-white border rounded-2xl p-4 space-y-3">
            <h2 className="font-bold">准許定性 — {selected.name}（{selected.code}）</h2>
            <p className="text-xs text-stone-500">
              白名單語義：勾選的定性可寄、未勾的不可寄。<span className="font-bold text-stone-700">全不勾＝准許全部</span>（例：香港本地）。
              判定鏈：商品定性 → 類目定性 → 其他。
            </p>
            <div className="flex flex-wrap gap-2">
              {natures.length === 0 ? (
                <p className="text-sm text-stone-400">定性字典載入中（migration 0019 後可用）…</p>
              ) : natures.map((n) => {
                const draft = draftFor(selected);
                const checked = draft ? draft.has(n.id) : false;
                return (
                  <label key={n.id} className={`flex items-center gap-1.5 border rounded-xl px-3 py-2 text-sm cursor-pointer ${checked ? 'border-x1493-primary bg-x1493-primary/5 text-stone-800' : 'border-stone-200 text-stone-500'}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleNature(selected, n.id)}
                      className="accent-x1493-primary"
                    />
                    {n.nameZh}
                  </label>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => saveNatures(selected)} className="btn-primary text-sm px-4 py-2 rounded-xl">保存准許定性</button>
              <button type="button" onClick={() => clearNatures(selected)} className="text-sm text-stone-500 px-3 py-2 rounded-xl border hover:text-stone-700">重設為准許全部</button>
              {natureDraft?.has(selected.id) && <span className="text-xs text-amber-600">有未保存的修改</span>}
            </div>
          </div>

          {/* 線種 */}
          <div className="bg-white border rounded-2xl p-4 space-y-3">
            <h2 className="font-bold">線種 — {selected.name}（{selected.code}）</h2>
            <form onSubmit={submitRate} className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
              <div>
                <label className={labelCls}>名稱</label>
                <input className={inputCls} value={rateForm.label} onChange={(e) => setRateForm({ ...rateForm, label: e.target.value })} placeholder="標準／加快" required />
              </div>
              <div>
                <label className={labelCls}>基本運費 HK$</label>
                <input className={inputCls} type="number" step="0.01" min="0" value={rateForm.baseHkd} onChange={(e) => setRateForm({ ...rateForm, baseHkd: e.target.value })} required />
              </div>
              <div>
                <label className={labelCls}>每 kg 加 HK$（可空）</label>
                <input className={inputCls} type="number" step="0.01" min="0" value={rateForm.perKgHkd} onChange={(e) => setRateForm({ ...rateForm, perKgHkd: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>時效（文字）</label>
                <input className={inputCls} value={rateForm.etaDays} onChange={(e) => setRateForm({ ...rateForm, etaDays: e.target.value })} placeholder="7-12" />
              </div>
              <div>
                <label className={labelCls}>排序</label>
                <input className={inputCls} type="number" value={rateForm.sort} onChange={(e) => setRateForm({ ...rateForm, sort: e.target.value })} />
              </div>
              <div className="flex gap-2">
                <button type="submit" className="btn-primary text-sm px-4 py-2 rounded-xl whitespace-nowrap">{editingRateId ? '更新' : '新增'}</button>
                {editingRateId && (
                  <button type="button" onClick={() => { setEditingRateId(null); setRateForm(emptyRateForm); }}
                    className="text-sm text-stone-500 px-3 py-2 rounded-xl border">取消</button>
                )}
              </div>
            </form>
            <div className="space-y-1.5">
              {selected.rates.length === 0 ? (
                <p className="text-sm text-stone-400">此分區尚無線種</p>
              ) : selected.rates.map((r) => (
                <div key={r.id} className="flex items-center justify-between bg-stone-50 rounded-xl px-3 py-2 text-sm">
                  <div>
                    <span className="font-bold">{r.label}</span>
                    <span className="ml-3 text-stone-600">{formatPrice(r.baseCents)}{r.perKgCents != null ? ` ＋ ${formatPrice(r.perKgCents)}/kg` : ''}</span>
                    {r.etaDays && <span className="ml-3 text-stone-400">時效 {r.etaDays}</span>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditingRateId(r.id); setRateForm({ label: r.label, baseHkd: (r.baseCents / 100).toString(), perKgHkd: r.perKgCents != null ? (r.perKgCents / 100).toString() : '', etaDays: r.etaDays || '', sort: String(r.sort) }); }}
                      className="p-1.5 text-stone-400 hover:text-x1493-primary" title="編輯"><Pencil size={15} /></button>
                    <button onClick={() => deleteRate(r)} className="p-1.5 text-stone-400 hover:text-red-500" title="刪除"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 商品定性覆寫（空=繼承類目） */}
      <div className="bg-white border rounded-2xl p-4 space-y-3">
        <h2 className="font-bold flex items-center gap-2"><Package size={18} className="text-x1493-primary" /> 商品物流定性</h2>
        <p className="text-xs text-stone-500">
          商品定性預設繼承類目；急凍/液體等個別情況可在此覆寫（例：掛在常溫類目但實際需冷鏈的雞髀肉 → 冷鏈食品）。
        </p>
        {productNatures.length === 0 ? (
          <p className="text-sm text-stone-400">尚無商品（或 migration 0019 未應用）</p>
        ) : (
          <div className="space-y-1.5">
            {productNatures.map((p) => {
              const overrideValue = natureOverrides.get(p.id) ?? '';
              return (
                <div key={p.id} className="flex items-center justify-between bg-stone-50 rounded-xl px-3 py-2 text-sm gap-3">
                  <div className="min-w-0">
                    <span className={`font-bold truncate ${p.isActive ? '' : 'text-stone-400 line-through'}`}>{p.name}</span>
                    <span className="ml-2 text-xs text-stone-400">{p.categoryName || p.categorySlug || '無類目'}</span>
                    {!p.isActive && <span className="ml-2 text-[11px] text-stone-400">（已下架）</span>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <select
                      value={overrideValue}
                      onChange={(e) => setNatureOverrides(new Map(natureOverrides).set(p.id, e.target.value))}
                      className="border border-stone-300 rounded-xl px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-x1493-primary/30"
                    >
                      <option value="">繼承類目（{p.categoryName ? '生效：' : ''}{natureName(p.effectiveNatureCode)}）</option>
                      {natures.map((n) => <option key={n.id} value={n.id}>{n.nameZh}</option>)}
                    </select>
                    <button
                      onClick={() => saveProductNature(p.id)}
                      disabled={String(p.overrideNatureId ?? '') === overrideValue}
                      className="text-xs px-3 py-1.5 rounded-xl border border-stone-300 text-stone-600 hover:border-x1493-primary hover:text-x1493-primary disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      保存
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
